import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { JobPosting, JobFormData, FormField } from './types';
import JobForm from './JobForm';
import ApplicationFormBuilder from './ApplicationFormBuilder';

interface JobCreationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: number;
  editingJob: JobPosting | null;
  formData: JobFormData;
  setFormData: React.Dispatch<React.SetStateAction<JobFormData>>;
  applicationFormFields: FormField[];
  setApplicationFormFields: React.Dispatch<React.SetStateAction<FormField[]>>;
  onSubmit: (e: React.FormEvent) => void;
  onStepBack: () => void;
  loading: boolean;
  businessId: string;
}

const JobCreationDialog: React.FC<JobCreationDialogProps> = ({
  isOpen,
  onClose,
  currentStep,
  editingJob,
  formData,
  setFormData,
  applicationFormFields,
  setApplicationFormFields,
  onSubmit,
  onStepBack,
  loading,
  businessId
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingJob ? 'Edit Job Posting' : 'Create New Job Posting'} - Step {currentStep} of 2
          </DialogTitle>
          <DialogDescription>
            {currentStep === 1 
              ? (editingJob ? 'Update the job posting details.' : 'Fill in the job details, then customize the application form.')
              : 'Customize the application form that candidates will see.'
            }
          </DialogDescription>
        </DialogHeader>
        
        {currentStep === 1 ? (
          <JobForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={onSubmit}
            onCancel={onClose}
            isEditing={!!editingJob}
            businessId={businessId}
          />
        ) : (
          <>
            <ApplicationFormBuilder
              formData={formData}
              applicationFormFields={applicationFormFields}
              setApplicationFormFields={setApplicationFormFields}
              onSubmit={onSubmit}
              onBack={onStepBack}
              onCancel={onClose}
              loading={loading}
              isEditing={!!editingJob}
            />
            
            {currentStep === 2 && (
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  onClick={onStepBack}
                >
                  ← Back to Job Details
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  onClick={onSubmit}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (editingJob ? 'Update Job Posting' : 'Create Job Posting')}
                </button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JobCreationDialog;